import { useCallback } from 'react'
import { resizeGroup } from './groupOperations'

/**
 * Creates the handleMouseMove function with all necessary dependencies
 * @param {Object} params - Object containing all required state and functions
 * @returns {Function} The handleMouseMove callback function
 */
export const createHandleMouseMove = ({
  isPageLocked,
  canvasRef,
  isDrawing,
  drawingMode,
  lineStart,
  setDrawingPath,
  isDragging,
  selectedElement,
  dragOffset,
  settemplate_content,
  currentPageIndex,
  isResizing,
  resizeHandle,
  currentPage,
  isRotating,
  rotationStart,
  zoomLevel // Add zoomLevel parameter
}) => {
  return useCallback((e) => {
    if (isPageLocked) return

    if (!canvasRef.current) return
    const rect = canvasRef.current.getBoundingClientRect()
    const scale = zoomLevel / 100
    // Adjust mouse coordinates for zoom level
    const mouseX = (e.clientX - rect.left) / scale
    const mouseY = (e.clientY - rect.top) / scale

    // Handle drawing
    if (isDrawing) {
      // accumulate points for freehand tools
      if (drawingMode === 'line' && lineStart) {
        // show live line preview by updating last point
        setDrawingPath([{ x: lineStart.x, y: lineStart.y }, { x: mouseX, y: mouseY }])
      } else {
        setDrawingPath(prev => [...prev, { x: mouseX, y: mouseY }])
      }
      return
    }

    if (isDragging && selectedElement) {
      const newX = mouseX - dragOffset.x
      const newY = mouseY - dragOffset.y

      settemplate_content(prev => ({
        ...prev,
        pages: prev.pages.map((page, index) =>
          index === currentPageIndex
            ? {
              ...page,
              items: page.items.map(item => {
                // Check if this is a PowerClip container with content to update
                if (item.isPowerClipContainer && item.powerClipContents) {
                  const updatedContents = item.powerClipContents.map(content =>
                    content.id === selectedElement
                      ? { ...content, x: newX - item.x, y: newY - item.y } // Relative to container
                      : content
                  )
                  // If any content was updated, return updated container
                  if (updatedContents.some((content, index) => content !== item.powerClipContents[index])) {
                    return { ...item, powerClipContents: updatedContents }
                  }
                }
                // Check if this is a group with a child to update
                if (item.type === 'group' && item.children) {
                  const updatedChildren = item.children.map(child =>
                    child.id === selectedElement
                      ? { ...child, x: newX - item.x, y: newY - item.y } // Relative to group
                      : child
                  )
                  if (updatedChildren.some((child, index) => child !== item.children[index])) {
                    return { ...item, children: updatedChildren }
                  }
                }
                // Regular element update
                return item.id === selectedElement
                  ? { ...item, x: newX, y: newY }
                  : item
              })
            }
            : page
        )
      }))
    } else if (isResizing && selectedElement && resizeHandle) {
      // Find element - could be regular element, PowerClip content, or group child
      let element = currentPage?.items?.find(item => item.id === selectedElement)
      let container = null

      // If not found as regular element, check PowerClip contents and group children
      if (!element) {
        for (const item of currentPage?.items || []) {
          if (item.isPowerClipContainer && item.powerClipContents) {
            const foundContent = item.powerClipContents.find(content => content.id === selectedElement)
            if (foundContent) {
              element = foundContent
              container = item
              break
            }
          }
          if (item.type === 'group' && item.children) {
            const foundChild = item.children.find(child => child.id === selectedElement)
            if (foundChild) {
              element = foundChild
              container = item
              break
            }
          }
        }
      }

      if (element) {
        let newWidth = element.width
        let newHeight = element.height
        let newX = element.x
        let newY = element.y

        // For PowerClip content, convert mouse coordinates to container-relative coordinates
        let effectiveMouseX = mouseX
        let effectiveMouseY = mouseY
        let effectiveElementX = element.x
        let effectiveElementY = element.y

        if (container) {
          // Convert absolute mouse coordinates to container-relative coordinates
          effectiveMouseX = mouseX - container.x
          effectiveMouseY = mouseY - container.y
          effectiveElementX = element.x
          effectiveElementY = element.y
        }

        switch (resizeHandle) {
          case 'se': // bottom-right
            newWidth = Math.max(20, effectiveMouseX - effectiveElementX)
            newHeight = Math.max(20, effectiveMouseY - effectiveElementY)
            break
          case 'sw': // bottom-left
            newWidth = Math.max(20, effectiveElementX + element.width - effectiveMouseX)
            newHeight = Math.max(20, effectiveMouseY - effectiveElementY)
            newX = Math.min(effectiveMouseX, effectiveElementX + element.width - 20)
            break
          case 'ne': // top-right
            newWidth = Math.max(20, effectiveMouseX - effectiveElementX)
            newHeight = Math.max(20, effectiveElementY + element.height - effectiveMouseY)
            newY = Math.min(effectiveMouseY, effectiveElementY + element.height - 20)
            break
          case 'nw': // top-left
            newWidth = Math.max(20, effectiveElementX + element.width - effectiveMouseX)
            newHeight = Math.max(20, effectiveElementY + element.height - effectiveMouseY)
            newX = Math.min(effectiveMouseX, effectiveElementX + element.width - 20)
            newY = Math.min(effectiveMouseY, effectiveElementY + element.height - 20)
            break
          case 'e': // right
            newWidth = Math.max(20, effectiveMouseX - effectiveElementX)
            break
          case 'w': // left
            newWidth = Math.max(20, effectiveElementX + element.width - effectiveMouseX)
            newX = Math.min(effectiveMouseX, effectiveElementX + element.width - 20)
            break
          case 's': // bottom
            newHeight = Math.max(20, effectiveMouseY - effectiveElementY)
            break
          case 'n': // top
            newHeight = Math.max(20, effectiveElementY + element.height - effectiveMouseY)
            newY = Math.min(effectiveMouseY, effectiveElementY + element.height - 20)
            break
          default:
            // No resize handle specified
            break
        }

        // Maintain aspect ratio for images when using corner handles (Shift key behavior)
        if ((element.type === 'image' || element.type === 'mask') && ['se', 'sw', 'ne', 'nw'].includes(resizeHandle)) {
          const aspectRatio = element.width / element.height
          const newAspectRatio = newWidth / newHeight

          // Adjust dimensions to maintain aspect ratio (use the smaller dimension change)
          if (newAspectRatio > aspectRatio) {
            // Width is too large, adjust it
            newWidth = newHeight * aspectRatio

            // Adjust position for left-side handles
            if (resizeHandle === 'sw' || resizeHandle === 'nw') {
              newX = effectiveElementX + element.width - newWidth
            }
          } else if (newAspectRatio < aspectRatio) {
            // Height is too large, adjust it
            newHeight = newWidth / aspectRatio

            // Adjust position for top-side handles
            if (resizeHandle === 'ne' || resizeHandle === 'nw') {
              newY = effectiveElementY + element.height - newHeight
            }
          }
        }

        settemplate_content(prev => ({
          ...prev,
          pages: prev.pages.map((page, index) =>
            index === currentPageIndex
              ? {
                ...page,
                items: page.items.map(item => {
                  // Check if this is a PowerClip container with content to update
                  if (item.isPowerClipContainer && item.powerClipContents) {
                    const updatedContents = item.powerClipContents.map(content =>
                      content.id === selectedElement
                        ? {
                            ...content,
                            width: newWidth,
                            height: newHeight,
                            // For PowerClip content, position is already relative to container
                            // Only update position if it actually changed (for resize handles that move position)
                            x: newX,
                            y: newY
                          }
                        : content
                    )
                    // If any content was updated, return updated container
                    if (updatedContents.some((content, index) => content !== item.powerClipContents[index])) {
                      return { ...item, powerClipContents: updatedContents }
                    }
                  }

                  // Check if this is a group with a child to update
                  if (item.type === 'group' && item.children) {
                    const updatedChildren = item.children.map(child =>
                      child.id === selectedElement
                        ? {
                            ...child,
                            width: newWidth,
                            height: newHeight,
                            x: newX,
                            y: newY
                          }
                        : child
                    )
                    if (updatedChildren.some((child, index) => child !== item.children[index])) {
                      return { ...item, children: updatedChildren }
                    }
                  }

                  // Special case: PowerClip container resize - scale all contents proportionally
                  if (item.id === selectedElement && item.isPowerClipContainer && item.powerClipContents) {
                    // Ensure we have valid dimensions to prevent division by zero
                    const oldWidth = element.width || 1
                    const oldHeight = element.height || 1
                    const scaleX = newWidth / oldWidth
                    const scaleY = newHeight / oldHeight

                    const scaledContents = item.powerClipContents.map(content => ({
                      ...content,
                      // Scale dimensions proportionally
                      width: Math.max(1, content.width * scaleX),
                      height: Math.max(1, content.height * scaleY),
                      // Scale position relative to container origin
                      x: content.x * scaleX,
                      y: content.y * scaleY,
                      // Scale font size for text elements (use minimum scale to maintain readability)
                      fontSize: content.type === 'text' ? Math.max(8, (content.fontSize || 16) * Math.min(scaleX, scaleY)) : content.fontSize
                    }))

                    return { ...item, width: newWidth, height: newHeight, x: newX, y: newY, powerClipContents: scaledContents }
                  }

                  // Special handling for freehand elements - scale path coordinates
                  if (item.id === selectedElement && item.type === 'freehand' && item.pathData) {
                    const scaleX = newWidth / element.width;
                    const scaleY = newHeight / element.height;

                    // Scale the relative path coordinates
                    const scaledPathData = {
                      ...item.pathData,
                      nodes: item.pathData.nodes.map(node => ({
                        ...node,
                        p: {
                          x: node.p.x * scaleX,
                          y: node.p.y * scaleY
                        },
                        h1: node.h1 ? {
                          x: node.h1.x * scaleX,
                          y: node.h1.y * scaleY
                        } : undefined,
                        h2: node.h2 ? {
                          x: node.h2.x * scaleX,
                          y: node.h2.y * scaleY
                        } : undefined
                      }))
                    };

                    return {
                      ...item,
                      width: newWidth,
                      height: newHeight,
                      x: newX,
                      y: newY,
                      pathData: scaledPathData
                    };
                  }

                  // Special handling for group elements - scale children proportionally
                  if (item.id === selectedElement && item.type === 'group') {
                    const oldWidth = element.width || 1
                    const oldHeight = element.height || 1
                    const scaleX = newWidth / oldWidth
                    const scaleY = newHeight / oldHeight

                    const resized = resizeGroup(item, scaleX, scaleY)
                    return { ...resized, x: newX, y: newY }
                  }

                  // Regular element update
                  return item.id === selectedElement
                    ? { ...item, width: newWidth, height: newHeight, x: newX, y: newY }
                    : item
                })
              }
              : page
          )
        }))
      }
    } else if (isRotating && selectedElement) {
      let element = currentPage?.items?.find(item => item.id === selectedElement)
      let parentGroup = null

      // If not found as top-level item, check inside groups
      if (!element) {
        for (const item of currentPage?.items || []) {
          if (item.type === 'group' && item.children) {
            const foundChild = item.children.find(child => child.id === selectedElement)
            if (foundChild) {
              element = foundChild
              parentGroup = item
              break
            }
          }
        }
      }

      if (element) {
        const centerX = (parentGroup ? parentGroup.x + element.x : element.x) + element.width / 2
        const centerY = (parentGroup ? parentGroup.y + element.y : element.y) + element.height / 2

        // Mouse coordinates are already adjusted for zoom
        const angle = Math.atan2(mouseY - centerY, mouseX - centerX) * (180 / Math.PI)
        const newRotation = angle - rotationStart.angle + rotationStart.initialRotation

        settemplate_content(prev => ({
          ...prev,
          pages: prev.pages.map((page, index) =>
            index === currentPageIndex
              ? {
                ...page,
                items: page.items.map(item => {
                  // Check if this is a group with a child to update
                  if (item.type === 'group' && item.children) {
                    const updatedChildren = item.children.map(child =>
                      child.id === selectedElement
                        ? { ...child, rotation: newRotation }
                        : child
                    )
                    if (updatedChildren.some((child, idx) => child !== item.children[idx])) {
                      return { ...item, children: updatedChildren }
                    }
                  }
                  return item.id === selectedElement
                    ? { ...item, rotation: newRotation }
                    : item
                })
              }
              : page
          )
        }))
      }
    }
  }, [
    isPageLocked,
    canvasRef,
    isDrawing,
    drawingMode,
    lineStart,
    setDrawingPath,
    isDragging,
    selectedElement,
    dragOffset,
    settemplate_content,
    currentPageIndex,
    isResizing,
    resizeHandle,
    currentPage,
    isRotating,
    rotationStart
  ])
}
